import { GeneratedSkedApiClient } from "../generated-client";
import type { paths } from "../generated/sked-api-openapi-types";
import {
  logUploadBreadcrumb,
  logUploadFailure,
  withUploadSpan,
} from "./upload-observability";
import { resolveMediaCorrelationKey } from "utils/media-observability";
import {
  isUploadAbortedError,
  UploadAbortedError,
  UploadProgressBytes,
} from "./types";

const S3_UPLOAD_TIMEOUT_MS = 15 * 60 * 1000;

export type S3UploadProgressCallback = (
  progress: number,
  bytes?: UploadProgressBytes
) => void;

type GetSignedUrlOperation = paths["/content-inbox/get-signed-url"]["post"];
type GetSignedUrlBody = NonNullable<
  GetSignedUrlOperation["requestBody"]
>["content"]["application/json"] & {
  observability?: {
    uploadAttemptId?: string;
    mediaCorrelationKey?: string;
  };
};
type GeneratedGetSignedUrlResponse =
  GetSignedUrlOperation["responses"][200]["content"]["application/json"] & {
    uploadUrl?: string;
    fileUrl?: string;
    mimeType?: string;
  };

function getGeneratedSkedApiClient() {
  return new GeneratedSkedApiClient<paths>();
}

export function uploadBlobToSignedUrl(
  uploadUrl: string,
  blob: Blob,
  mimeType?: string,
  onProgress?: S3UploadProgressCallback,
  observability?: {
    uploadAttemptId?: string;
    mediaCorrelationKey?: string;
    fileName?: string;
  },
  signal?: AbortSignal
) {
  const metadata = {
    ...observability,
    uploadUrl,
    blobSize: blob.size,
    mimeType,
  };

  logUploadBreadcrumb("media_upload.signed_url_put.start", metadata);

  return withUploadSpan(
    "media_upload.signed_url_put",
    metadata,
    async () =>
      new Promise<void>((resolve, reject) => {
        if (!(blob instanceof Blob)) {
          reject(
            new Error(
              `Expected Blob for S3 upload, received ${Object.prototype.toString.call(blob)}`
            )
          );
          return;
        }

        if (signal?.aborted) {
          reject(new UploadAbortedError("S3 upload request aborted"));
          return;
        }

        const xhr = new XMLHttpRequest();
        xhr.open("PUT", uploadUrl, true);
        xhr.timeout = S3_UPLOAD_TIMEOUT_MS;

        const handleAbortSignal = () => xhr.abort();
        signal?.addEventListener("abort", handleAbortSignal, { once: true });
        const releaseAbortSignal = () =>
          signal?.removeEventListener("abort", handleAbortSignal);

        if (mimeType) {
          xhr.setRequestHeader("Content-Type", mimeType);
        }

        let lastLoggedProgressBucket = -1;

        xhr.upload.addEventListener("progress", (event) => {
          const total = event.total || blob.size;
          if (!total) {
            return;
          }
          const progress = Math.min(
            100,
            ((event.loaded / total) * 100 + 100) / 2
          );
          const progressBucket = Math.floor(progress / 10);
          if (progressBucket > lastLoggedProgressBucket) {
            lastLoggedProgressBucket = progressBucket;
            logUploadBreadcrumb("media_upload.signed_url_put.progress", {
              ...metadata,
              loaded: event.loaded,
              total,
              progress,
            });
          }
          onProgress?.(progress, { loaded: event.loaded, total });
        });

        xhr.addEventListener("load", () => {
          releaseAbortSignal();
          if (xhr.status >= 200 && xhr.status < 300) {
            logUploadBreadcrumb("media_upload.signed_url_put.success", {
              ...metadata,
              status: xhr.status,
            });
            resolve();
            return;
          }

          logUploadFailure(
            "media_upload.signed_url_put.failure",
            new Error(
              `S3 upload failed with status ${xhr.status}: ${xhr.responseText || "empty response"}`
            ),
            {
              ...metadata,
              httpStatus: xhr.status,
            }
          );
          reject(
            new Error(
              `S3 upload failed with status ${xhr.status}: ${xhr.responseText || "empty response"}`
            )
          );
        });

        xhr.onerror = () => {
          releaseAbortSignal();
          logUploadFailure(
            "media_upload.signed_url_put.failure",
            new Error("S3 upload request failed"),
            metadata
          );
          reject(new Error("S3 upload request failed"));
        };

        xhr.addEventListener("abort", () => {
          releaseAbortSignal();
          reject(new UploadAbortedError("S3 upload request aborted"));
        });

        xhr.ontimeout = () => {
          releaseAbortSignal();
          logUploadFailure(
            "media_upload.signed_url_put.failure",
            new Error("S3 upload request timed out"),
            metadata
          );
          reject(new Error("S3 upload request timed out"));
        };

        // The send below can throw synchronously (InvalidStateError and
        // friends), which rejects on its own but leaks the abort listener.
        try {
          xhr.send(blob);
        } catch (error) {
          releaseAbortSignal();
          logUploadFailure(
            "media_upload.signed_url_put.failure",
            error,
            metadata
          );
          reject(error);
        }
      })
  );
}

export async function uploadFileToS3(
  {
    blob,
    fileName,
    uploadAttemptId,
    mediaCorrelationKey,
  }: {
    blob: Blob;
    fileName: string;
    uploadAttemptId?: string;
    mediaCorrelationKey?: string;
  },
  options?: {
    onProgress: S3UploadProgressCallback;
    signal?: AbortSignal;
  }
) {
  const metadata = {
    uploadAttemptId,
    fileName,
    blobSize: blob.size,
    mediaCorrelationKey:
      mediaCorrelationKey ??
      resolveMediaCorrelationKey({ uploadAttemptId, fileName }),
  };

  return withUploadSpan(
    "media_upload.upload_file_to_s3",
    metadata,
    async () => {
      try {
        const { uploadUrl, fileUrl, mimeType } = await getSignedUrl(fileName, {
          uploadAttemptId,
          mediaCorrelationKey: metadata.mediaCorrelationKey,
        });
        logUploadBreadcrumb("media_upload.get_signed_url.success", {
          ...metadata,
          fileUrl,
          mimeType,
        });
        await uploadBlobToSignedUrl(
          uploadUrl,
          blob,
          mimeType,
          options?.onProgress,
          {
            uploadAttemptId,
            mediaCorrelationKey: metadata.mediaCorrelationKey,
            fileName,
          },
          options?.signal
        );
        logUploadBreadcrumb("media_upload.upload_file_to_s3.success", {
          ...metadata,
          fileUrl,
        });
        return fileUrl;
      } catch (error) {
        if (!isUploadAbortedError(error)) {
          logUploadFailure(
            "media_upload.upload_file_to_s3.failure",
            error,
            metadata
          );
        }
        throw error;
      }
    }
  );
}

interface GetSignedUrlResponse {
  fileUrl: string;
  uploadUrl: string;
  mimeType?: string;
}

async function getSignedUrl(
  fileName: string,
  observability?: {
    uploadAttemptId?: string;
    mediaCorrelationKey?: string;
  }
): Promise<GetSignedUrlResponse> {
  const metadata = { fileName, ...observability };

  logUploadBreadcrumb("media_upload.get_signed_url.start", metadata);

  return withUploadSpan("media_upload.get_signed_url", metadata, async () => {
    const generatedClient = getGeneratedSkedApiClient();
    const result = await generatedClient.client.POST(
      "/content-inbox/get-signed-url",
      {
        body: {
          fileName,
          observability,
        } as GetSignedUrlBody,
      }
    );
    const response = (await generatedClient.unwrap(
      result
    )) as GeneratedGetSignedUrlResponse;

    return {
      uploadUrl: response.uploadUrl ?? response.url ?? "",
      fileUrl: response.fileUrl ?? response.params?.FinalUrl ?? "",
      mimeType: response.mimeType ?? response.params?.ContentType,
    };
  });
}
